// Reporte Total General: todas las carreras (central, app y bot) y los mensajes de WhatsApp.
// Los datos salen de Postgres (espejo de Firebase) a traves de /api/reporte-total-general;
// esta pantalla no lee Firebase.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { useTheme } from '../ThemeContext';
import { cabeceraSesion } from '../utils/sesionPanel';

const ZONA = 'America/Guayaquil';
const DIAS_SEMANA = ['', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];

// Fecha AAAA-MM-DD en hora de Ecuador
const ymd = (fecha) => new Intl.DateTimeFormat('en-CA', { timeZone: ZONA }).format(fecha);
const sumarDias = (texto, n) => {
  const d = new Date(`${texto}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
};

const rangoDe = (preset) => {
  const hoy = ymd(new Date());
  const [a, m] = hoy.split('-').map(Number);
  const inicioMes = `${a}-${String(m).padStart(2, '0')}-01`;
  switch (preset) {
    case 'hoy': return [hoy, hoy];
    case 'ayer': return [sumarDias(hoy, -1), sumarDias(hoy, -1)];
    case '7dias': return [sumarDias(hoy, -6), hoy];
    case '30dias': return [sumarDias(hoy, -29), hoy];
    case 'mesAnterior': {
      const fin = sumarDias(inicioMes, -1);
      return [`${fin.slice(0, 7)}-01`, fin];
    }
    case 'anio': return [`${a}-01-01`, hoy];
    case 'mes':
    default: return [inicioMes, hoy];
  }
};

const n = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString('es-EC'));
const pct = (parte, total) => (total ? `${((parte / total) * 100).toFixed(1)}%` : '—');
const fechaCorta = (dia) => new Date(`${dia}T12:00:00Z`).toLocaleDateString('es-EC', { weekday: 'short', day: '2-digit', month: 'short', timeZone: 'UTC' });
const NOMBRE_SUBCANAL = {
  llamada_convencional: 'Llamada convencional', llamada_celular: 'Central · celular', bot_ycloud: 'WhatsApp bot YCloud',
  bot_whatsapp_web: 'WhatsApp bot Web', reserva: 'Reserva'
};

function Tarjeta({ titulo, valor, detalle, color }) {
  const { colors } = useTheme();
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: '14px 16px', flex: '1 1 170px', minWidth: 160 }}>
      <div style={{ fontSize: 11, letterSpacing: 0.6, textTransform: 'uppercase', color: colors.textSecondary }}>{titulo}</div>
      <div style={{ fontSize: 28, fontWeight: 700, color: color || colors.text, margin: '4px 0' }}>{valor}</div>
      {detalle ? <div style={{ fontSize: 12, color: colors.textSecondary }}>{detalle}</div> : null}
    </div>
  );
}

function Seccion({ titulo, subtitulo, children, acciones }) {
  const { colors } = useTheme();
  return (
    <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 16, color: colors.text }}>{titulo}</h3>
          {subtitulo ? <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>{subtitulo}</div> : null}
        </div>
        {acciones}
      </div>
      {children}
    </div>
  );
}

// Tabla simple con columnas { clave, titulo, formato?, alinear? } y altura maxima opcional
function Tabla({ columnas, filas, alto, pie }) {
  const { colors } = useTheme();
  const celda = { padding: '7px 10px', borderBottom: `1px solid ${colors.border}`, fontSize: 13, color: colors.text };
  return (
    <div style={{ maxHeight: alto || 'none', overflow: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columnas.map(c => (
              <th key={c.clave} style={{ ...celda, position: 'sticky', top: 0, background: colors.surface, fontSize: 11, textTransform: 'uppercase', color: colors.textSecondary, textAlign: c.alinear || 'right', whiteSpace: 'nowrap' }}>
                {c.titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => (
            <tr key={i}>
              {columnas.map(c => (
                <td key={c.clave} style={{ ...celda, textAlign: c.alinear || 'right', fontWeight: c.negrita ? 700 : 400, whiteSpace: c.alinear === 'left' ? 'normal' : 'nowrap' }}>
                  {c.formato ? c.formato(f[c.clave], f) : (typeof f[c.clave] === 'number' ? n(f[c.clave]) : (f[c.clave] ?? '—'))}
                </td>
              ))}
            </tr>
          ))}
          {pie ? (
            <tr>
              {columnas.map(c => (
                <td key={c.clave} style={{ ...celda, textAlign: c.alinear || 'right', fontWeight: 700 }}>
                  {typeof pie[c.clave] === 'number' ? n(pie[c.clave]) : (pie[c.clave] ?? '')}
                </td>
              ))}
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

// Barras verticales (CSS) con detalle al pasar el cursor
function Barras({ datos, etiqueta, valor, detalle, color = '#3b82f6', alto = 160 }) {
  const { colors } = useTheme();
  const max = Math.max(1, ...datos.map(valor));
  const pocas = datos.length <= 31;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: datos.length > 40 ? 1 : 3, height: alto, borderBottom: `1px solid ${colors.border}` }}>
        {datos.map((d, i) => (
          <div key={i} title={detalle(d)} style={{ flex: 1, height: `${(valor(d) / max) * 100}%`, minHeight: valor(d) ? 2 : 0, background: color, borderRadius: '3px 3px 0 0' }} />
        ))}
      </div>
      <div style={{ display: 'flex', gap: datos.length > 40 ? 1 : 3, fontSize: 10, color: colors.textSecondary, marginTop: 4 }}>
        {datos.map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            {pocas || i % Math.ceil(datos.length / 15) === 0 ? etiqueta(d) : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

function ReporteTotalGeneral() {
  const { colors } = useTheme();
  const [preset, setPreset] = useState('mes');
  const [desde, setDesde] = useState(rangoDe('mes')[0]);
  const [hasta, setHasta] = useState(rangoDe('mes')[1]);
  const [horaDesde, setHoraDesde] = useState('00:00');
  const [horaHasta, setHoraHasta] = useState('23:59');
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [tablaDia, setTablaDia] = useState(false);
  const [tablaHora, setTablaHora] = useState(false);
  const [ordenUnidad, setOrdenUnidad] = useState('carreras');
  const [buscaUnidad, setBuscaUnidad] = useState('');

  const cargar = useCallback(async (d = desde, h = hasta, hd = horaDesde, hh = horaHasta) => {
    setCargando(true);
    setError(null);
    try {
      const r = await fetch(`/api/reporte-total-general?desde=${d}&hasta=${h}&horaDesde=${hd}&horaHasta=${hh}`, { headers: await cabeceraSesion() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.ok) throw new Error(j.message || `Error ${r.status}`);
      setDatos(j);
    } catch (e) {
      setError(e.message);
    } finally {
      setCargando(false);
    }
  }, [desde, hasta, horaDesde, horaHasta]);

  useEffect(() => { cargar(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cambiarPreset = (p) => {
    setPreset(p);
    if (p !== 'personalizado') {
      const [d, h] = rangoDe(p);
      setDesde(d);
      setHasta(h);
      setHoraDesde('00:00');
      setHoraHasta('23:59');
      cargar(d, h, '00:00', '23:59');
    }
  };

  // Mensajes por dia combinando WhatsApp Web y YCloud
  const mensajes = useMemo(() => {
    if (!datos) return { filas: [], totales: {} };
    const porDia = {};
    const fila = (dia) => (porDia[dia] = porDia[dia] || { dia, webRecibidos: 0, webEnviados: 0, webClientesRec: 0, webConductoresEnv: 0, ycloudEnviados: 0, ycloudFallidos: 0 });
    (datos.mensajes.whatsappWeb || []).forEach(m => {
      const f = fila(m.dia);
      f.webRecibidos += m.recibidos || 0;
      f.webEnviados += m.enviados || 0;
      if (m.sesion === 'clientes') f.webClientesRec += m.recibidos || 0;
      if (m.sesion === 'conductores') f.webConductoresEnv += m.enviados || 0;
    });
    (datos.mensajes.ycloud || []).forEach(m => {
      const f = fila(m.dia);
      f.ycloudEnviados += m.enviados;
      f.ycloudFallidos += m.fallidos;
    });
    const filas = Object.values(porDia).sort((a, b) => (a.dia < b.dia ? 1 : -1));
    const totales = filas.reduce((t, f) => {
      Object.keys(f).forEach(k => { if (k !== 'dia') t[k] = (t[k] || 0) + f[k]; });
      return t;
    }, {});
    const porLinea = {};
    (datos.mensajes.whatsappWeb || []).forEach(m => {
      porLinea[m.sesion] = porLinea[m.sesion] || { recibidos: 0, enviados: 0 };
      porLinea[m.sesion].recibidos += m.recibidos || 0;
      porLinea[m.sesion].enviados += m.enviados || 0;
    });
    return { filas, totales, porLinea };
  }, [datos]);

  const unidades = useMemo(() => {
    if (!datos) return [];
    const b = buscaUnidad.trim().toLowerCase();
    return [...datos.porUnidad]
      .filter(u => !b || String(u.unidad).includes(b) || String(u.conductor || '').toLowerCase().includes(b))
      .sort((a, c) => {
        if (ordenUnidad === 'rechazo') return (c.avisadas ? c.rechazadas / c.avisadas : -1) - (a.avisadas ? a.rechazadas / a.avisadas : -1);
        if (ordenUnidad === 'unidad') return Number(a.unidad) - Number(c.unidad);
        return (c[ordenUnidad] || 0) - (a[ordenUnidad] || 0);
      });
  }, [datos, ordenUnidad, buscaUnidad]);

  // ---------- Exportar ----------
  const exportarExcel = () => {
    if (!datos) return;
    const libro = XLSX.utils.book_new();
    const r = datos.resumen;
    const hoja = (nombre, filas) => XLSX.utils.book_append_sheet(libro, XLSX.utils.json_to_sheet(filas), nombre);
    hoja('Resumen', [
      ['Desde', `${datos.desde} ${datos.horaDesde}`], ['Hasta', `${datos.hasta} ${datos.horaHasta}`], ['Carreras', r.total], ['Atendidas', r.atendidas], ['No atendidas', r.no_atendidas],
      ['Canceladas', r.canceladas], ['Llamada convencional', r.llamada_convencional], ['Central celular', r.llamada_celular],
      ['WhatsApp', r.whatsapp], ['Reservas', r.reservas], ['Automáticas (app)', r.automaticas], ['Manuales (central)', r.manuales],
      ['WhatsApp atendidas por el sistema', r.whatsapp_atendidas_sistema], ['WhatsApp rescatadas por la central', r.whatsapp_rescatadas_central],
      ['Clientes únicos', r.clientes_unicos], ['Unidades activas', r.unidades_activas], ['Llegada promedio (min)', r.tiempo_llegada_promedio]
    ].map(([Indicador, Valor]) => ({ Indicador, Valor })));
    hoja('Por día', datos.porDia);
    hoja('Por hora', datos.porHora);
    hoja('Por conductor', datos.porUnidad);
    hoja('Por operadora', datos.porOperadora);
    hoja('Operadora por día', datos.operadoraPorDia);
    hoja('Sectores', datos.porSector);
    hoja('Clientes', datos.clientes.top);
    hoja('Mensajes WhatsApp', mensajes.filas);
    XLSX.writeFile(libro, `reporte-total-general_${datos.desde}_${datos.hasta}.xlsx`);
  };

  const exportarPdf = async () => {
    if (!datos) return;
    const { jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;
    const pdf = new jsPDF({ orientation: 'landscape' });
    const r = datos.resumen;
    pdf.setFontSize(14);
    pdf.text(`Reporte Total General · Plaza del Valle · ${datos.desde} ${datos.horaDesde} a ${datos.hasta} ${datos.horaHasta}`, 14, 14);
    autoTable(pdf, {
      startY: 20, head: [['Carreras', 'Atendidas', 'No atendidas', 'Canceladas', 'Convencional', 'Celular', 'WhatsApp', 'Reservas', 'Automáticas', 'Manuales', 'Clientes', 'Unidades']],
      body: [[r.total, r.atendidas, r.no_atendidas, r.canceladas, r.llamada_convencional, r.llamada_celular, r.whatsapp, r.reservas, r.automaticas, r.manuales, r.clientes_unicos, r.unidades_activas]]
    });
    autoTable(pdf, {
      startY: pdf.lastAutoTable.finalY + 6, head: [['Día', 'Total', 'Atendidas', 'No atendidas', 'Canceladas', 'WhatsApp', 'Convencional', 'Celular', 'Automáticas', 'Manuales', 'Unidades']],
      body: datos.porDia.map(d => [d.dia, d.total, d.atendidas, d.no_atendidas, d.canceladas, d.whatsapp, d.llamada_convencional, d.llamada_celular, d.automaticas, d.manuales, d.unidades])
    });
    autoTable(pdf, {
      startY: pdf.lastAutoTable.finalY + 6, head: [['Operadora', 'Total', 'Atendidas', 'Canceladas', 'Convencional', 'Celular', 'WhatsApp']],
      body: datos.porOperadora.map(o => [o.operadora, o.total, o.atendidas, o.canceladas, o.convencional, o.celular, o.whatsapp])
    });
    autoTable(pdf, {
      startY: pdf.lastAutoTable.finalY + 6, head: [['Unidad', 'Conductor', 'Carreras', 'Automáticas', 'Manuales', 'WhatsApp', 'Avisadas', 'Rechazadas', 'Llegada (min)']],
      body: datos.porUnidad.map(u => [u.unidad, u.conductor || '', u.carreras, u.automaticas, u.manuales, u.whatsapp, u.avisadas, u.rechazadas, u.tiempo_llegada ?? ''])
    });
    pdf.save(`reporte-total-general_${datos.desde}_${datos.hasta}.pdf`);
  };

  const control = { padding: '7px 10px', borderRadius: 6, border: `1px solid ${colors.border}`, background: colors.background, color: colors.text, fontSize: 13 };
  const boton = (fondo) => ({ ...control, background: fondo, color: '#fff', border: 'none', fontWeight: 600, cursor: 'pointer' });
  const fila = { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 16 };
  const subtitulo = { margin: '22px 0 10px', fontSize: 18, color: colors.text };

  const r = datos && datos.resumen;
  const dias = datos ? Math.max(1, r.dias_con_carreras) : 1;

  return (
    <div style={{ padding: 24, maxWidth: 1400 }}>
      <h2 style={{ margin: 0, color: colors.text }}>Reporte Total General</h2>
      <div style={{ fontSize: 12, color: colors.textSecondary, margin: '4px 0 14px' }}>
        Todas las carreras (central, reservas, app y bot de WhatsApp) y los mensajes de WhatsApp. Se consulta en la base propia (Postgres), no en Firebase.
        {datos?.actualizacion?.ultima_sincronizacion ? ` Última sincronización: ${new Date(datos.actualizacion.ultima_sincronizacion).toLocaleString('es-EC')}.` : ''}
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 18 }}>
        <select value={preset} onChange={e => cambiarPreset(e.target.value)} style={control}>
          <option value="hoy">Hoy</option>
          <option value="ayer">Ayer</option>
          <option value="7dias">Últimos 7 días</option>
          <option value="30dias">Últimos 30 días</option>
          <option value="mes">Este mes</option>
          <option value="mesAnterior">Mes anterior</option>
          <option value="anio">Este año</option>
          <option value="personalizado">Personalizado</option>
        </select>
        <input type="date" value={desde} onChange={e => { setDesde(e.target.value); setPreset('personalizado'); }} style={control} />
        <input type="time" value={horaDesde} onChange={e => { setHoraDesde(e.target.value || '00:00'); setPreset('personalizado'); }} style={control} title="Hora desde" />
        <span style={{ color: colors.textSecondary }}>a</span>
        <input type="date" value={hasta} onChange={e => { setHasta(e.target.value); setPreset('personalizado'); }} style={control} />
        <input type="time" value={horaHasta} onChange={e => { setHoraHasta(e.target.value || '23:59'); setPreset('personalizado'); }} style={control} title="Hora hasta" />
        <button onClick={() => cargar()} disabled={cargando} style={boton('#3b82f6')}>{cargando ? 'Calculando…' : '↻ Actualizar'}</button>
        <button onClick={exportarExcel} disabled={!datos} style={boton('#16a34a')}>⬇ Excel</button>
        <button onClick={exportarPdf} disabled={!datos} style={boton('#dc2626')}>⬇ PDF</button>
      </div>

      {error && <div style={{ padding: 12, borderRadius: 8, background: '#fee2e2', color: '#991b1b', marginBottom: 16 }}>{error}</div>}
      {!datos && cargando && <div style={{ color: colors.textSecondary }}>Calculando el reporte…</div>}

      {datos && (
        <>
          <div style={fila}>
            <Tarjeta titulo="Carreras" valor={n(r.total)} detalle={`${n(Math.round(r.total / dias))} por día en promedio`} />
            <Tarjeta titulo="Atendidas" valor={n(r.atendidas)} detalle={`${pct(r.atendidas, r.total)} del total · ${n(r.finalizadas)} cerradas en la app`} color="#16a34a" />
            <Tarjeta titulo="No atendidas" valor={n(r.no_atendidas)} detalle={`${pct(r.no_atendidas, r.total)} del total`} color="#dc2626" />
            <Tarjeta titulo="Canceladas" valor={n(r.canceladas)} detalle={pct(r.canceladas, r.total)} />
            <Tarjeta titulo="Clientes únicos" valor={n(r.clientes_unicos)} detalle={`${n(datos.clientes.recurrentes)} pidieron más de una vez · ${n(datos.clientes.primera_vez)} nuevos`} />
            <Tarjeta titulo="Unidades activas" valor={n(r.unidades_activas)} detalle={`Llegada promedio: ${r.tiempo_llegada_promedio ?? '—'} min`} />
          </div>

          <h3 style={subtitulo}>¿Por dónde entran las carreras?</h3>
          <div style={fila}>
            <Tarjeta titulo="Llamada convencional" valor={n(r.llamada_convencional)} detalle={`${pct(r.llamada_convencional, r.total)} · teléfono de 7-8 dígitos`} />
            <Tarjeta titulo="Central · celular" valor={n(r.llamada_celular)} detalle={`${pct(r.llamada_celular, r.total)} · llamada o mensaje a la central`} />
            <Tarjeta titulo="WhatsApp (ubicación)" valor={n(r.whatsapp)} detalle={`${pct(r.whatsapp, r.total)} · bot YCloud ${n(r.whatsapp_ycloud)} · bot WhatsApp Web ${n(r.whatsapp_web)}`} />
            <Tarjeta titulo="Reservas" valor={n(r.reservas)} detalle={pct(r.reservas, r.total)} />
          </div>

          <h3 style={subtitulo}>¿Quién asigna la unidad?</h3>
          <div style={fila}>
            <Tarjeta titulo="Automático (app)" valor={n(r.automaticas)} detalle={`${pct(r.automaticas, r.automaticas + r.manuales)} de las asignadas · el conductor aceptó en la app`} />
            <Tarjeta titulo="Manual (central)" valor={n(r.manuales)} detalle={`${pct(r.manuales, r.automaticas + r.manuales)} de las asignadas · la operadora la asignó`} />
            <Tarjeta titulo="WhatsApp atendidas por el sistema" valor={n(r.whatsapp_atendidas_sistema)} detalle={`${pct(r.whatsapp_atendidas_sistema, r.whatsapp)} de las de WhatsApp`} />
            <Tarjeta titulo="WhatsApp rescatadas por la central" valor={n(r.whatsapp_rescatadas_central)} detalle={`${pct(r.whatsapp_rescatadas_central, r.whatsapp)} de las de WhatsApp · ${n(r.retiradas)} pedidos retirados para crear una carrera manual`} />
            <Tarjeta titulo="WhatsApp sin unidad" valor={n(r.whatsapp_no_atendidas)} detalle={`${pct(r.whatsapp_no_atendidas, r.whatsapp)} de las de WhatsApp`} color="#dc2626" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
            <Seccion titulo="Carreras por día" subtitulo="Pase el cursor sobre una barra para ver el detalle."
              acciones={<button style={control} onClick={() => setTablaDia(v => !v)}>{tablaDia ? 'Ver gráfico' : 'Ver como tabla'}</button>}>
              {tablaDia ? (
                <Tabla alto={240} filas={datos.porDia} columnas={[{ clave: 'dia', titulo: 'Día', alinear: 'left', formato: fechaCorta }, { clave: 'total', titulo: 'Total' }, { clave: 'atendidas', titulo: 'Atendidas' }, { clave: 'whatsapp', titulo: 'WhatsApp' }]} />
              ) : (
                <Barras datos={datos.porDia} valor={d => d.total} etiqueta={d => d.dia.slice(8, 10) + '/' + d.dia.slice(5, 7)}
                  detalle={d => `${fechaCorta(d.dia)}: ${d.total} carreras · ${d.atendidas} atendidas · ${d.whatsapp} WhatsApp · ${d.llamada_convencional} convencional · ${d.llamada_celular} celular`} />
              )}
            </Seccion>
            <Seccion titulo="Carreras por hora del día" subtitulo="Suma de todo el período: muestra las horas pico."
              acciones={<button style={control} onClick={() => setTablaHora(v => !v)}>{tablaHora ? 'Ver gráfico' : 'Ver como tabla'}</button>}>
              {(() => {
                const horas = Array.from({ length: 24 }, (_, h) => datos.porHora.find(x => x.hora === h) || { hora: h, total: 0, whatsapp: 0, central: 0, no_atendidas: 0 });
                return tablaHora ? (
                  <Tabla alto={240} filas={horas} columnas={[{ clave: 'hora', titulo: 'Hora', alinear: 'left', formato: v => `${String(v).padStart(2, '0')}:00` }, { clave: 'total', titulo: 'Total' }, { clave: 'central', titulo: 'Central' }, { clave: 'whatsapp', titulo: 'WhatsApp' }, { clave: 'no_atendidas', titulo: 'No atendidas' }]} />
                ) : (
                  <Barras datos={horas} valor={d => d.total} etiqueta={d => (d.hora % 2 === 0 ? d.hora : '')}
                    detalle={d => `${String(d.hora).padStart(2, '0')}:00 · ${d.total} carreras (${d.central} central, ${d.whatsapp} WhatsApp)`} />
                );
              })()}
            </Seccion>
          </div>

          <Seccion titulo="Detalle por día">
            <Tabla alto={420} filas={[...datos.porDia].reverse()} columnas={[
              { clave: 'dia', titulo: 'Día', alinear: 'left', formato: fechaCorta },
              { clave: 'total', titulo: 'Total', negrita: true },
              { clave: 'atendidas', titulo: 'Atendidas' },
              { clave: 'no_atendidas', titulo: 'No atendidas' },
              { clave: 'canceladas', titulo: 'Canceladas' },
              { clave: 'whatsapp', titulo: 'WhatsApp' },
              { clave: 'llamada_convencional', titulo: 'Convencional' },
              { clave: 'llamada_celular', titulo: 'Celular' },
              { clave: 'reservas', titulo: 'Reservas' },
              { clave: 'automaticas', titulo: 'Automáticas' },
              { clave: 'manuales', titulo: 'Manuales' },
              { clave: 'unidades', titulo: 'Unidades' },
              { clave: 'clientes', titulo: 'Clientes' }
            ]} pie={datos.porDia.reduce((t, d) => {
              ['total', 'atendidas', 'no_atendidas', 'canceladas', 'whatsapp', 'llamada_convencional', 'llamada_celular', 'reservas', 'automaticas', 'manuales'].forEach(k => { t[k] = (t[k] || 0) + d[k]; });
              return t;
            }, { dia: 'TOTAL', unidades: r.unidades_activas, clientes: r.clientes_unicos })} />
          </Seccion>

          <Seccion titulo="Carreras por conductor"
            subtitulo="Avisadas y rechazadas son los pedidos de WhatsApp que le llegaron a la app del conductor."
            acciones={(
              <div style={{ display: 'flex', gap: 8 }}>
                <input placeholder="Buscar unidad o nombre" value={buscaUnidad} onChange={e => setBuscaUnidad(e.target.value)} style={control} />
                <select value={ordenUnidad} onChange={e => setOrdenUnidad(e.target.value)} style={control}>
                  <option value="carreras">Ordenar por carreras</option>
                  <option value="automaticas">Automáticas</option>
                  <option value="manuales">Manuales</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="avisadas">Avisadas</option>
                  <option value="rechazo">% rechazo</option>
                  <option value="unidad">Número de unidad</option>
                </select>
              </div>
            )}>
            <Tabla alto={460} filas={unidades} columnas={[
              { clave: 'unidad', titulo: 'Unidad', alinear: 'left', negrita: true },
              { clave: 'conductor', titulo: 'Conductor', alinear: 'left' },
              { clave: 'carreras', titulo: 'Carreras', negrita: true },
              { clave: 'automaticas', titulo: 'Automáticas' },
              { clave: 'manuales', titulo: 'Manuales' },
              { clave: 'whatsapp', titulo: 'WhatsApp' },
              { clave: 'convencional', titulo: 'Convencional' },
              { clave: 'celular', titulo: 'Celular' },
              { clave: 'canceladas', titulo: 'Canceladas' },
              { clave: 'avisadas', titulo: 'Avisadas' },
              { clave: 'rechazadas', titulo: 'Rechazadas' },
              { clave: 'rechazo', titulo: '% rechazo', formato: (v, f) => (f.avisadas ? pct(f.rechazadas, f.avisadas) : '—') },
              { clave: 'tiempo_llegada', titulo: 'Llegada (min)', formato: v => v ?? '—' }
            ]} />
          </Seccion>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: 16 }}>
            <Seccion titulo="Por operadora" subtitulo="Carreras que registró o asignó cada operadora. 'Automático' = aceptadas por el conductor en la app o sin operadora registrada.">
              <Tabla filas={datos.porOperadora} columnas={[
                { clave: 'operadora', titulo: 'Operadora', alinear: 'left', negrita: true },
                { clave: 'total', titulo: 'Total', negrita: true },
                { clave: 'atendidas', titulo: 'Atendidas' },
                { clave: 'canceladas', titulo: 'Canceladas' },
                { clave: 'convencional', titulo: 'Convencional' },
                { clave: 'celular', titulo: 'Celular' },
                { clave: 'whatsapp', titulo: 'WhatsApp' },
                { clave: 'reservas', titulo: 'Reservas' },
                { clave: 'dias', titulo: 'Días' }
              ]} />
            </Seccion>
            <Seccion titulo="Bot de WhatsApp" subtitulo="Pedidos que entraron enviando la ubicación.">
              <Tabla filas={[
                { k: 'Pedidos creados', y: datos.botYcloud.pedidos, w: datos.botWeb.pedidos },
                { k: 'Aceptados por un conductor en la app', y: datos.botYcloud.aceptados_app, w: datos.botWeb.aceptados_app },
                { k: 'Rescatados por la central (asignación manual)', y: datos.botYcloud.rescatados_central, w: datos.botWeb.rescatados_central },
                { k: 'Sin conductor (no atendidos)', y: datos.botYcloud.no_atendidos, w: datos.botWeb.no_atendidos },
                { k: 'Cancelados', y: datos.botYcloud.cancelados, w: datos.botWeb.cancelados },
                { k: 'Conductores avisados por pedido', y: datos.botYcloud.conductores_avisados_promedio, w: datos.botWeb.conductores_avisados_promedio }
              ]} columnas={[{ clave: 'k', titulo: '', alinear: 'left' }, { clave: 'y', titulo: 'YCloud' }, { clave: 'w', titulo: 'WhatsApp Web' }]} />
            </Seccion>
          </div>

          <Seccion titulo="Operadoras por día" subtitulo="Carreras de cada operadora en cada día.">
            {(() => {
              const nombres = datos.porOperadora.map(o => o.operadora);
              const porDia = {};
              datos.operadoraPorDia.forEach(x => { (porDia[x.dia] = porDia[x.dia] || { dia: x.dia })[x.operadora] = x.total; });
              const filas = Object.values(porDia).sort((a, b) => (a.dia < b.dia ? 1 : -1));
              return <Tabla alto={360} filas={filas} columnas={[{ clave: 'dia', titulo: 'Día', alinear: 'left', formato: fechaCorta }, ...nombres.map(o => ({ clave: o, titulo: o, formato: v => (v ? n(v) : '—') }))]} />;
            })()}
          </Seccion>

          <h3 style={subtitulo}>Mensajes de WhatsApp</h3>
          <div style={fila}>
            <Tarjeta titulo="WhatsApp Web · recibidos" valor={n(mensajes.totales.webRecibidos || 0)} detalle={`Clientes: ${n(mensajes.porLinea?.clientes?.recibidos || 0)} · Conductores: ${n(mensajes.porLinea?.conductores?.recibidos || 0)}`} />
            <Tarjeta titulo="WhatsApp Web · enviados" valor={n(mensajes.totales.webEnviados || 0)} detalle={`Clientes (bot): ${n(mensajes.porLinea?.clientes?.enviados || 0)} · Conductores: ${n(mensajes.porLinea?.conductores?.enviados || 0)}`} />
            <Tarjeta titulo="YCloud · enviados" valor={n(mensajes.totales.ycloudEnviados || 0)} detalle="Bot anterior (cobrados por YCloud)" />
            <Tarjeta titulo="YCloud · fallidos" valor={n(mensajes.totales.ycloudFallidos || 0)} detalle="No se cobran" />
          </div>
          {datos.mensajes.whatsappWeb === null && (
            <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: -8, marginBottom: 12 }}>El servicio de WhatsApp Web no respondió: sus mensajes no se incluyen.</div>
          )}
          <Seccion titulo="Mensajes por día" subtitulo="WhatsApp Web se cuenta desde el 5 de octubre de 2026, cuando se activaron las líneas.">
            <Tabla alto={320} filas={mensajes.filas} columnas={[
              { clave: 'dia', titulo: 'Día', alinear: 'left', formato: fechaCorta },
              { clave: 'webRecibidos', titulo: 'WhatsApp Web recibidos' },
              { clave: 'webEnviados', titulo: 'WhatsApp Web enviados' },
              { clave: 'ycloudEnviados', titulo: 'YCloud enviados' },
              { clave: 'ycloudFallidos', titulo: 'YCloud fallidos' }
            ]} />
          </Seccion>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 16 }}>
            <Seccion titulo="Sectores con más carreras">
              <Tabla alto={360} filas={datos.porSector} columnas={[
                { clave: 'sector', titulo: 'Sector', alinear: 'left' },
                { clave: 'total', titulo: 'Total', negrita: true },
                { clave: 'whatsapp', titulo: 'WhatsApp' },
                { clave: 'no_atendidas', titulo: 'No atendidas' }
              ]} />
            </Seccion>
            <Seccion titulo="Clientes que más piden" subtitulo={`${n(datos.clientes.unicos)} clientes · ${n(datos.clientes.recurrentes)} repitieron · ${n(datos.clientes.primera_vez)} sin carreras antes del período (registro desde ${datos.actualizacion.primera_carrera || '—'})`}>
              <Tabla alto={360} filas={datos.clientes.top} columnas={[
                { clave: 'nombre', titulo: 'Cliente', alinear: 'left' },
                { clave: 'telefono', titulo: 'Teléfono', alinear: 'left' },
                { clave: 'tipo', titulo: 'Tipo', alinear: 'left' },
                { clave: 'carreras', titulo: 'Carreras', negrita: true }
              ]} />
            </Seccion>
            <Seccion titulo="Forma de pago, estados y taxímetro">
              <Tabla filas={[
                ...datos.porEmpresa.map(e => ({ k: `Pago: ${e.tipo}`, v: n(e.total) })),
                ...datos.porEstado.map(e => ({ k: `Estado en el sistema: ${e.estado}`, v: n(e.total) })),
                { k: 'Taxímetro: viajes medidos', v: n(datos.taximetro.finalizadas) },
                { k: 'Taxímetro: cobrado', v: datos.taximetro.cobrado != null ? `$${Number(datos.taximetro.cobrado).toFixed(2)}` : '—' },
                { k: 'Taxímetro: promedio por viaje', v: datos.taximetro.promedio != null ? `$${Number(datos.taximetro.promedio).toFixed(2)}` : '—' }
              ]} columnas={[{ clave: 'k', titulo: '', alinear: 'left' }, { clave: 'v', titulo: '' }]} />
            </Seccion>
          </div>

          <Seccion titulo="Carreras por día de la semana" subtitulo="Promedio de carreras en cada día de la semana dentro del período.">
            <Tabla filas={datos.porDiaSemana.map(d => ({ ...d, nombre: DIAS_SEMANA[d.dia_semana], promedio: d.dias ? Math.round(d.total / d.dias) : 0 }))} columnas={[
              { clave: 'nombre', titulo: 'Día', alinear: 'left' },
              { clave: 'total', titulo: 'Carreras' },
              { clave: 'dias', titulo: 'Días en el período' },
              { clave: 'promedio', titulo: 'Promedio por día', negrita: true }
            ]} />
          </Seccion>

          <div style={{ fontSize: 12, color: colors.textSecondary, lineHeight: 1.6 }}>
            <b>Cómo se cuenta:</b> cada carrera una sola vez (las copias de pedidosManuales y NotificaciOnenCurso no se suman).
            Convencional = teléfono de 7-8 dígitos; celular = 9 o más. WhatsApp = pedidos del bot por ubicación.
            Automática = el conductor aceptó el pedido del bot en la app; manual = la operadora asignó la unidad.
            La central de Plaza no guarda sus cancelaciones, por eso las canceladas son casi todas del bot.
            Canales: {Object.entries(NOMBRE_SUBCANAL).map(([k, v]) => v).join(', ')}.
          </div>
        </>
      )}
    </div>
  );
}

export default ReporteTotalGeneral;
